// components/Sidebar.js
// Redesigned as a top navigation bar to match the Subtidal dashboard aesthetic
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Avatar, Menu, MenuItem, Divider,
  IconButton, Collapse, List, ListItem, ListItemText,
  CircularProgress
} from '@mui/material';
import {
  KeyboardArrowDown as DownIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import subtidal_logo from '../logo.svg';

const NAV_BG = '#263B4B';
const NAV_HOVER = 'rgba(255,255,255,0.08)';
const NAV_ACTIVE = 'rgba(255,255,255,0.14)';
const NAV_TEXT = 'rgba(255,255,255,0.85)';
const NAV_TEXT_DIM = 'rgba(255,255,255,0.5)';
const ACCENT = '#61D38A';

const navItemStyle = (isActive) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  px: 1.5,
  py: 0.75,
  borderRadius: '6px',
  cursor: 'pointer',
  color: isActive ? '#fff' : NAV_TEXT,
  bgcolor: isActive ? NAV_ACTIVE : 'transparent',
  transition: 'background 0.15s ease, color 0.15s ease',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {
    bgcolor: NAV_HOVER,
    color: '#fff',
  },
});

// ─── Dropdown Menu Component ─────────────────────────────────────────────────
const NavDropdown = ({ label, items, onNavigate, isActive, anchorOrigin, loading, error }) => {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);

  return (
    <>
      <Box
        sx={navItemStyle(isActive)}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem', lineHeight: 1 }}>
          {label}
        </Typography>
        <DownIcon sx={{ fontSize: 16, opacity: 0.7, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: '#1e2d40',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            borderRadius: '8px',
            minWidth: 200,
            mt: 0.5,
          }
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {loading ? (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          </Box>
        ) : error ? (
          <MenuItem disabled sx={{ color: '#f44336', fontSize: '0.8rem' }}>{error}</MenuItem>
        ) : items.length === 0 ? (
          <MenuItem disabled sx={{ color: NAV_TEXT_DIM, fontSize: '0.8rem' }}>None available</MenuItem>
        ) : (
          items.map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => { onNavigate(item.path); setAnchor(null); }}
              sx={{
                color: NAV_TEXT,
                fontSize: '0.875rem',
                py: 1,
                px: 2,
                borderRadius: '4px',
                mx: 0.5,
                '&:hover': { bgcolor: NAV_HOVER, color: '#fff' },
                '&.Mui-selected': { bgcolor: NAV_ACTIVE },
              }}
            >
              {item.label}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

// ─── Main Sidebar (now TopNav) ────────────────────────────────────────────────
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout, user, grids, gridsLoading, gridsError,
    sensorPacks, sensorPacksLoading, sensorPacksError
  } = useAuth();

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  const getUserInitial = () => user?.username?.charAt(0).toUpperCase() || 'U';
  const getUserDisplayName = () => user?.username || user?.email?.split('@')[0] || 'User';

  const pondGrids = grids.filter(g => g.name.toLowerCase().includes('pond'));

  const handleLogout = () => {
    setUserMenuAnchor(null);
    logout();
    navigate('/login');
  };

  const handleAccountSettings = () => {
    setUserMenuAnchor(null);
    navigate('/account');
  };

  const isSection = (path) => location.pathname.startsWith(path);

  const gridItems = grids.map(g => ({ id: g.id, label: g.name || `Grid ${g.id}`, path: `/grids/${g.id}` }));
  const pondItems = pondGrids.map(g => ({ id: g.id, label: g.name || `Grid ${g.id}`, path: `/ponds/${g.id}` }));
  const sensorItems = sensorPacks.map(s => ({ id: s.id, label: s.description || s.name || `Sensor ${s.id}`, path: `/sensor-packs/${s.id}` }));

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        bgcolor: NAV_BG,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        boxShadow: '0 1px 12px rgba(0,0,0,0.3)',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        px: 2,
        gap: 1,
      }}
    >
      {/* Logo */}
      <Box
        onClick={() => navigate('/')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mr: 2,
          flexShrink: 0,
          '&:hover': { opacity: 0.85 },
        }}
      >
        <img src={subtidal_logo} alt="Subtidal" style={{ height: 26, width: 'auto' }} />
      </Box>

      {/* Divider */}
      <Box sx={{ width: '1px', height: 20, bgcolor: 'rgba(255,255,255,0.12)', mr: 1 }} />

      {/* Navigation Items */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>

        {/* Admin: Grids dropdown */}
        {user?.is_admin && (
          <NavDropdown
            label="Grids"
            items={gridItems}
            onNavigate={navigate}
            isActive={isSection('/grids')}
            loading={gridsLoading}
            error={gridsError}
          />
        )}


        {/* Non-admin with multiple grids: Ponds dropdown */}
        {!user?.is_admin && grids.length > 1 && (
          <NavDropdown
            label="Lakes"
            items={pondItems}
            onNavigate={navigate}
            isActive={isSection('/ponds')}
            loading={gridsLoading}
            error={gridsError}
          />
        )}

        {/* Non-admin with single grid: direct link */}
        {!user?.is_admin && grids.length === 1 && (
          <Box
            sx={navItemStyle(isSection('/ponds') || isSection('/grids'))}
            onClick={() => navigate(`/ponds/${grids[0].id}`)}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              {grids[0].name || 'My Pond'}
            </Typography>
          </Box>
        )}

        {/* Admin: Sensor Packs dropdown */}
        {user?.is_admin && (
          <NavDropdown
            label="Sensor Packs"
            items={sensorItems}
            onNavigate={navigate}
            isActive={isSection('/sensor-packs')}
            loading={sensorPacksLoading}
            error={sensorPacksError}
          />
        )}

        {/* Admin: External links */}
        {user?.is_admin && (
          <>
            <Box
              sx={navItemStyle(isSection('/hq-buoys'))}
              onClick={() => navigate('/hq-buoys')}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>HQ Buoys</Typography>
            </Box>
            <Box
              sx={navItemStyle(false)}
              onClick={() => window.open('https://api.subtidal.com/admin', '_blank')}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Admin</Typography>
            </Box>
          </>
        )}
      </Box>

      {/* User Avatar + Menu */}
      <Box
        onClick={(e) => setUserMenuAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          px: 1,
          py: 0.5,
          borderRadius: '6px',
          '&:hover': { bgcolor: NAV_HOVER },
          transition: 'background 0.15s ease',
          flexShrink: 0,
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: ACCENT,
            fontSize: '0.75rem',
            fontWeight: 700,
          }}
        >
          {getUserInitial()}
        </Avatar>
        <Typography variant="body2" sx={{ color: NAV_TEXT, fontSize: '0.8rem', display: { xs: 'none', sm: 'block' } }}>
          {getUserDisplayName()}
        </Typography>
        {user?.is_admin && (
          <Typography variant="caption" sx={{ color: ACCENT, fontSize: '0.7rem', display: { xs: 'none', md: 'block' } }}>
            Admin
          </Typography>
        )}
        <DownIcon sx={{ fontSize: 16, color: NAV_TEXT_DIM }} />
      </Box>

      <Menu
        anchorEl={userMenuAnchor}
        open={userMenuOpen}
        onClose={() => setUserMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: '#1e2d40',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            borderRadius: '8px',
            minWidth: 180,
            mt: 0.5,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>{getUserDisplayName()}</Typography>
          {user?.is_admin && (
            <Typography variant="caption" sx={{ color: ACCENT }}>Administrator</Typography>
          )}
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <MenuItem
          onClick={handleAccountSettings}
          sx={{
            color: NAV_TEXT,
            fontSize: '0.875rem',
            py: 1,
            px: 2,
            gap: 1.5,
            '&:hover': { bgcolor: NAV_HOVER, color: '#fff' },
          }}
        >
          <PersonIcon sx={{ fontSize: 16 }} />
          Account settings
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          sx={{
            color: NAV_TEXT,
            fontSize: '0.875rem',
            py: 1,
            px: 2,
            gap: 1.5,
            '&:hover': { bgcolor: NAV_HOVER, color: '#fff' },
          }}
        >
          <LogoutIcon sx={{ fontSize: 16 }} />
          Log out
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Sidebar;